// The account's Alexa email — `Users.EmailId`.
//
// ⚠️ IT MUST BE THE AMAZON ACCOUNT'S EMAIL, not "a Gmail". The skill
// (alxea-lambda capabilities_discover.js) trades the Alexa link token for the
// Amazon profile's email and discovers devices with
// GetAlexaDeviceListForDiscoverByEmailId `where c.EmailId = '<that email>'`.
// A typo, a blank, or a different address = Alexa finds NO devices, silently.
// Sign-up was the only place it could be typed until 2026-10-20.
//
// ⚠️ ONE ACCOUNT PER EMAIL — two Users rows with the same EmailId would merge
// both families' devices into one Alexa. The save refuses an address another
// account holds. (The lookup endpoint returns the whole row; only its UserId is
// read here, nothing of it is shown.)

import { post, updateUser } from '../api/api';

// Strict on purpose: the backend interpolates this into SQL, so nothing outside
// a plain address (no quotes, spaces, backslashes) may ever reach it.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/** Trimmed, lower-case — the way it is stored. */
export function normaliseEmail(v) {
  return String(v || '').trim().toLowerCase();
}

/** -> '' when fine, else what is wrong. */
export function emailProblem(v) {
  const e = normaliseEmail(v);
  if (!e) return 'Enter the email of your Amazon (Alexa) account.';
  if (e.length > 100 || !EMAIL_RE.test(e)) return 'That is not a valid email address.';
  return '';
}

/**
 * Save the account's Alexa email. -> { ok: true, email } | { ok: false, message }
 */
export async function saveAlexaEmail(userId, value) {
  const problem = emailProblem(value);
  if (problem) return { ok: false, message: problem };
  const email = normaliseEmail(value);
  try {
    const res = await post('GetUserDetailsbyEmailId', { EmailId: email });
    const rows = Array.isArray(res?.data) ? res.data : [];
    if (rows.some((r) => Number(r.UserId) !== Number(userId))) {
      return { ok: false, message: 'This email is already used by another account. Each account needs its own Amazon email.' };
    }
  } catch (e) {
    return { ok: false, message: 'Could not check the email right now. Please try again.' };
  }
  try {
    const res = await updateUser({ EmailId: email }, `UserId=${Number(userId)}`);
    if (res?.data && String(res.data.status) === '500') throw new Error('refused');
  } catch (e) {
    return { ok: false, message: 'Could not save the email. Please try again.' };
  }
  return { ok: true, email };
}
